import type { ForecastTableControlValues } from "../components/tableControls/forecastTableControlValues";
import { canAssertShapeKey, type ShapeEnvironment } from "./dataShape";
import type {
  SavedViewPayload,
  ViewDataShape,
  ViewRows
} from "./savedViewPayload";

/** Live state a view is compared against. */
export interface ViewComparison extends ShapeEnvironment {
  rows: ViewRows;
}

function sameStringList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

function sameRows(a: ViewRows, b: ViewRows): boolean {
  return (
    a.preset === b.preset &&
    a.sortBy === b.sortBy &&
    sameStringList(a.categories, b.categories) &&
    sameStringList(a.metrics, b.metrics) &&
    a.yearOffsets?.[0] === b.yearOffsets?.[0] &&
    a.yearOffsets?.[1] === b.yearOffsets?.[1]
  );
}

/**
 * Whether the live state has drifted from the view that is applied.
 *
 * Compares only what the view actually claims, and only what the bar could
 * take: a preset that speaks for ``planBy`` alone isn't dirtied by a period
 * switch, and a key ``mergeDataShape`` skipped isn't drift the user caused.
 * Layout is deliberately excluded: column widths change on every drag and
 * would leave the picker permanently marked.
 */
export function isViewDirty(
  payload: SavedViewPayload,
  live: ViewComparison
): boolean {
  const shape = payload.dataShape;
  if (shape) {
    for (const [key, value] of Object.entries(shape)) {
      if (value === undefined) continue;
      if (!canAssertShapeKey(key as keyof ViewDataShape, value, live)) continue;
      if (live.controls[key as keyof ForecastTableControlValues] !== value) {
        return true;
      }
    }
  }
  if (payload.rows && !sameRows(payload.rows, live.rows)) return true;
  return false;
}
